import { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import api, { imgUrl } from "@/lib/api";
import PublicNav from "@/components/PublicNav";
import InquiryForm from "@/components/InquiryForm";
import logo from "@/assets/logo.png";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { toast } from "sonner";
import { MapPin, BedDouble, Bath, Users, Check, ArrowLeft, Loader2, Sparkles, CalendarDays } from "lucide-react";

const FALLBACK = "https://images.pexels.com/photos/11400444/pexels-photo-11400444.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

function eachNight(checkIn, checkOut) {
  const days = [];
  let cur = new Date(checkIn + "T00:00:00");
  const end = new Date(checkOut + "T00:00:00");
  while (cur < end) { days.push(new Date(cur)); cur.setDate(cur.getDate() + 1); }
  return days;
}

export default function VillaDetail() {
  const { id } = useParams();
  const [villa, setVilla] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [active, setActive] = useState(0);
  const [booked, setBooked] = useState([]);
  const [range, setRange] = useState();

  useEffect(() => {
    api.get(`/public/properties/${id}`).then(({ data }) => { setVilla(data); document.title = `${data.name} | Magical Villas Orlando`; }).catch(() => setNotFound(true));
    api.get(`/public/properties/${id}/availability`).then(({ data }) => setBooked(data)).catch(() => {});
  }, [id]);

  const bookedDays = useMemo(() => booked.flatMap((r) => eachNight(r.check_in, r.check_out)), [booked]);

  const handleSelect = (r) => {
    if (r?.from && r?.to) {
      const crosses = bookedDays.some((d) => d >= r.from && d < r.to);
      if (crosses) {
        toast.error("Those dates include nights that are already booked. Please pick an open range.");
        setRange({ from: r.to });
        return;
      }
    }
    setRange(r);
  };

  if (notFound) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#FDFBF7] gap-4">
        <p className="font-display text-4xl text-[#1A365D]">Villa not found</p>
        <Link to="/" className="text-[#E07A5F] font-medium hover:underline">← Back to all villas</Link>
      </div>
    );
  }
  if (!villa) return <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]"><Loader2 className="animate-spin text-[#1A365D]" size={30} /></div>;

  const images = villa.images?.length ? villa.images : [FALLBACK];

  return (
    <div className="bg-[#FDFBF7] min-h-screen" data-testid="villa-detail">
      <div className="relative h-[60vh] min-h-[420px]">
        <img src={imgUrl(images[active])} alt={villa.name} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A365D]/80 to-[#1A365D]/20" />
        <PublicNav />
        <div className="relative z-20 max-w-7xl mx-auto px-6 sm:px-10 h-full flex flex-col justify-end pb-12">
          <Link to="/" data-testid="back-to-villas" className="text-white/80 hover:text-white text-sm flex items-center gap-1.5 mb-4 w-fit"><ArrowLeft size={16} /> All villas</Link>
          <h1 className="font-display text-5xl sm:text-6xl tracking-tight font-medium text-white">{villa.name}</h1>
          <p className="text-white/85 flex items-center gap-1.5 mt-2"><MapPin size={16} /> {villa.location}</p>
        </div>
      </div>

      {images.length > 1 && (
        <div className="max-w-7xl mx-auto px-6 sm:px-10 -mt-8 relative z-20 flex gap-3 overflow-x-auto pb-2">
          {images.map((img, i) => (
            <button key={i} onClick={() => setActive(i)} className={`shrink-0 w-28 h-20 rounded-xl overflow-hidden border-2 transition-all ${active === i ? "border-[#E07A5F]" : "border-white"}`}>
              <img src={imgUrl(img)} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}

      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-16 grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-12">
        <div>
          <div className="flex flex-wrap items-center gap-6 pb-8 border-b border-black/5 text-gray-700">
            <span className="flex items-center gap-2"><BedDouble size={22} className="text-[#1A365D]" /> {villa.bedrooms} Bedrooms</span>
            <span className="flex items-center gap-2"><Bath size={22} className="text-[#1A365D]" /> {villa.bathrooms} Bathrooms</span>
            <span className="flex items-center gap-2"><Users size={22} className="text-[#1A365D]" /> Sleeps {villa.max_guests}</span>
          </div>

          <h2 className="font-display text-3xl font-medium mt-10 mb-4">About this villa</h2>
          <p className="text-gray-600 leading-relaxed text-lg">{villa.description || "A beautiful Orlando getaway perfect for your family vacation."}</p>

          {villa.amenities?.length > 0 && (
            <>
              <h2 className="font-display text-3xl font-medium mt-12 mb-5">What's included</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {villa.amenities.map((a, i) => (
                  <span key={i} className="flex items-center gap-2 text-gray-700">
                    <span className="w-7 h-7 rounded-full bg-[#81B29A]/15 text-[#81B29A] flex items-center justify-center"><Check size={15} /></span> {a}
                  </span>
                ))}
              </div>
            </>
          )}

          <h2 className="font-display text-3xl font-medium mt-12 mb-2 flex items-center gap-2"><CalendarDays size={24} className="text-[#1A365D]" /> Availability</h2>
          <p className="text-gray-500 mb-5">Shaded dates are already booked. Click your check-in then check-out date — we'll add them to your inquiry automatically.</p>
          <div data-testid="availability-calendar" className="bg-white rounded-2xl border border-black/5 p-5 shadow-sm inline-block">
            <DayPicker
              mode="range"
              numberOfMonths={1}
              selected={range}
              onSelect={handleSelect}
              excludeDisabled
              disabled={[{ before: new Date() }, ...bookedDays]}
              modifiers={{ booked: bookedDays }}
              modifiersClassNames={{ booked: "rdp-day_booked" }}
              styles={{ day_today: { color: "#E07A5F", fontWeight: 700 } }}
            />
            <div className="flex items-center gap-5 mt-3 text-xs text-gray-500 px-1">
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-[#E3D5CA]" /> Booked</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-[#1A365D]" /> Selected</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm border border-[#81B29A] bg-white" /> Available</span>
            </div>
            {range?.from && (
              <div data-testid="selected-range" className="mt-3 flex items-center justify-between gap-3 bg-[#F4F1EA] rounded-xl px-4 py-2.5 text-sm">
                <span className="text-[#1A365D] font-medium">
                  {range.to ? `${range.from.toLocaleDateString("en-US", { month: "short", day: "numeric" })} → ${range.to.toLocaleDateString("en-US", { month: "short", day: "numeric" })}` : "Now pick your check-out date"}
                </span>
                <button type="button" data-testid="clear-dates" onClick={() => setRange(undefined)} className="text-gray-500 hover:text-[#E07A5F] text-xs font-medium">Clear</button>
              </div>
            )}
          </div>
        </div>

        <aside className="lg:sticky lg:top-8 h-fit">
          <div className="bg-white rounded-2xl border border-black/5 shadow-lg p-7">
            <p className="mb-1"><span className="font-display text-4xl text-[#1A365D]">${villa.price_per_night}</span><span className="text-gray-400"> / night</span></p>
            <p className="text-sm text-[#E07A5F] flex items-center gap-1.5 mb-6"><Sparkles size={15} /> Pick dates on the calendar or tell us below</p>
            <InquiryForm propertyId={villa.id} propertyName={villa.name} range={range} />
          </div>
        </aside>
      </div>

      <footer className="bg-[#142a49] text-white/60 py-10 text-center text-sm">
        <img src={logo} alt="Magical Villas Orlando" className="h-28 w-auto mx-auto mb-4" />
        © {new Date().getFullYear()} Magical Villas Orlando. All rights reserved.
      </footer>
    </div>
  );
}
